import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations from './translations';
import { useBrand } from './BrandContext';

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const brand = useBrand();
  // Saved user preference wins; otherwise the tenant's configured default language.
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('marketpulse-lang');
    if (saved) return saved;
    return brand.defaultLang || 'en';
  });

  const isRTL = lang === 'ar';

  useEffect(() => {
    localStorage.setItem('marketpulse-lang', lang);
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('data-lang', lang);
  }, [lang, isRTL]);

  const toggleLang = () => setLang(prev => (prev === 'en' ? 'ar' : 'en'));

  const t = useCallback((key) => {
    let val = translations[lang]?.[key] || translations.en?.[key] || key;
    // Dynamic project labels from brand config
    if (val === key && key.startsWith('wizProject_') && brand.projectLabels) {
      const projectKey = key.replace('wizProject_', '');
      const label = brand.projectLabels[projectKey];
      if (label) val = lang === 'ar' ? (label.ar || label.en) : label.en;
    }
    if (typeof val === 'string' && val.includes('{brandName}')) {
      const name = lang === 'ar' ? (brand.brandNameAr || brand.brandName) : brand.brandName;
      val = val.replace(/\{brandName\}/g, name);
    }
    if (typeof val === 'string' && val.includes('{brandDomain}')) {
      val = val.replace(/\{brandDomain\}/g, brand.brandDomain || '');
    }
    return val;
  }, [lang, brand]);

  return (
    <LanguageContext.Provider value={{ lang, isRTL, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
